import React, { useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { KTSVG } from '../../../../../_mexablock/helpers';
import clsx from 'clsx';
import { ConfirmModal } from './ConfirmModal';

type Props = {
  show: boolean;
  row: any;
  handleClose: () => void;
  openChangeStatus: (id: number, statusName: string) => void;
  openSendPortability?: (id: number) => void;
};

const RULES_BY_NAME: Record<string, string[]> = {
  'Pendiente': ['En ABD', 'No procesada'],
  'PEN': ['En ABD', 'No procesada'],
  'En ABD': ['Portada', 'No procesada'],
  'ABD': ['Portada', 'No procesada'],
  'Portada': [],
  'EXT': [],
  'Rechazada': [],
  'REC': [],
  'Error': [],
  'ERR': [],
  'No procesada': [],
  'NOP': [],
};

const PortabilityDetailModal: React.FC<Props> = ({ show, row, handleClose, openChangeStatus, openSendPortability }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  if (!row) return null;

  const first = row?.customer?.first_name ?? '';
  const last = row?.customer?.father_last_name ?? '';
  const fullName = `${first} ${last}`.trim() || 'Sin nombre';

  const history: any[] = row.history ?? [];

  const currentStatusName = row?.status?.name || row?.status?.code || '';
  const isNoProcesada = currentStatusName === 'No procesada' || currentStatusName === 'NOP' || row?.status_id === 42;
  const allowedTargets = RULES_BY_NAME[currentStatusName] || [];
  
  const handleStatusChange = (statusName: string) => {
    setShowDropdown(false);
    handleClose();
    openChangeStatus(row.id, statusName);
  };

  return (
    <>
      <Modal
        id="kt_modal_portability_detail"
        tabIndex={-1}
        aria-hidden="true"
        dialogClassName="modal-dialog modal-dialog-centered mw-600px"
        show={show}
        onHide={() => {
          setShowDropdown(false);
          handleClose();
        }}
      >
        <div className="modal-content">
          <div className="modal-header pb-0 border-0 justify-end">
            <div
              className="btn btn-sm btn-icon btn-active-color-primary"
              onClick={() => {
                setShowDropdown(false);
                handleClose();
              }}
            >
              <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
            </div>
          </div>
          <div className="modal-body scroll-y mx-5 pt-0">
            <h1 className="mb-8 fw-bolder text-dark">Detalles de portabilidad</h1>
            
            <h4 className="fw-bolder text-dark mb-4">Datos del cliente</h4>
            <div className="separator mb-4"></div>
            <div className="row mb-4">
              <div className="col-6">
                <div className="text-muted fw-semibold fs-7 mb-1">Nombre</div>
                <div className="text-dark fw-bolder fs-6">{fullName}</div>
              </div>
              <div className="col-6">
                <div className="text-muted fw-semibold fs-7 mb-1">Teléfono</div>
                <div className="text-dark fw-bolder fs-6">{row?.customer?.phone || '—'}</div>
              </div>
            </div>
            <div className="row mb-8">
              <div className="col-6">
                <div className="text-muted fw-semibold fs-7 mb-1">Contacto</div>
                <div className="text-dark fw-bolder fs-6">{row?.customer?.email || '—'}</div>
              </div>
            </div>

            <h4 className="fw-bolder text-dark mb-4">Datos de la línea</h4>
            <div className="separator mb-4"></div>
            <div className="row mb-4">
              <div className="col-6">
                <div className="text-muted fw-semibold fs-7 mb-1">Número a portar</div>
                <div className="text-dark fw-bolder fs-6">{row?.phone_number || '—'}</div>
              </div>
              <div className="col-6">
                <div className="text-muted fw-semibold fs-7 mb-1">ICCID</div>
                <div className="text-dark fw-bolder fs-6">{row?.iccid || '—'}</div>
              </div>
            </div>
            <div className="row mb-8">
              <div className="col-6">
                <div className="text-muted fw-semibold fs-7 mb-1">NIP</div>
                <div className="text-dark fw-bolder fs-6">{row?.nip || '—'}</div>
              </div>
              <div className="col-6">
                <div className="text-muted fw-semibold fs-7 mb-1">Fecha portabilidad</div>
                <div className="text-dark fw-bolder fs-6">{row?.date_portability ? new Date(row.date_portability).toLocaleDateString() : '—'}</div>
              </div>
            </div>
            <div className="row mb-8">
              <div className="col-6">
                <div className="text-muted fw-semibold fs-7 mb-1">Número transitorio</div>
                <div className="text-dark fw-bolder fs-6 text-primary">{row?.transitory_number || '—'}</div>
              </div>
            </div>

            <h4 className="fw-bolder text-dark mb-4">Historial de estatus</h4>
            <div className="separator mb-4"></div>
            <div className="mt-4">
              {history.length === 0 && (
                <p className="text-muted fs-7">Sin historial registrado.</p>
              )}
              {history.map((h, i) => {
                const isNewest = i === 0;
                const isOldest = i === history.length - 1;
                return (
                  <div className="pb-5" key={i} style={{ position: 'relative', paddingLeft: '36px' }}>
                    {!isOldest && (
                      <div style={{
                        position: 'absolute',
                        left: '8px',
                        top: '14px',
                        width: '2px',
                        bottom: '-4px',
                        backgroundColor: '#e4e6ef',
                        zIndex: 1
                      }} />
                    )}
                    <div style={{
                      position: 'absolute',
                      left: '0',
                      top: '4px',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: isNewest ? '#009ef7' : '#a1a5b7',
                      border: `4px solid ${isNewest ? '#e1f0ff' : '#ffffff'}`,
                      boxSizing: 'border-box',
                      zIndex: 2
                    }} />
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center mb-1">
                        <span className={`fw-bolder fs-5 me-3 ${isNewest ? 'text-primary' : 'text-dark'}`}>
                          {h.status?.name ?? h.status}
                        </span>
                        <span className="text-muted fs-7">
                          {new Date(h.createdAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                        </span>
                      </div>
                      <div className="text-muted fw-semibold fs-7">
                        Cambiado por: <span className="text-gray-800">{h.changedBy ? `${h.changedBy.first_name} ${h.changedBy.father_last_name}` : 'Sistema'}</span>
                      </div>
                      {h.comment && (
                        <div className="text-gray-700 mt-2 fs-7 bg-light rounded p-3">
                          {h.comment}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-end pt-10 d-flex justify-content-end gap-3 align-items-center">
              <button className="btn btn-light" onClick={() => handleClose()}>Cerrar</button>
              
              {isNoProcesada && openSendPortability && (
                <button
                  className="btn btn-primary fw-bolder"
                  onClick={() => {
                    handleClose();
                    openSendPortability(row.id);
                  }}
                >
                  Enviar a portar
                </button>
              )}

              {allowedTargets.length > 0 && (
                <div className="d-inline-block position-relative">
                  <button
                    className="btn btn-light btn-active-light-primary fw-bolder"
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    onClick={() => setShowDropdown(!showDropdown)}
                  >
                    Cambiar estatus ▾
                  </button>
                  {showDropdown && (
                    <div
                      className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold fs-7 w-200px py-4 d-block position-absolute"
                      style={{ right: 0, bottom: '100%', zIndex: 105, marginBottom: '5px' }}
                    >
                      {allowedTargets.map((target) => (
                        <div className="menu-item px-3" key={target}>
                          <a
                            className={clsx('menu-link px-3', { 'text-danger': target === 'No procesada' })}
                            href="#/"
                            onClick={(e) => {
                              e.preventDefault();
                              handleStatusChange(target);
                            }}
                          >
                            {target === 'No procesada' ? 'Rechazar (No procesada)' : target}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export { PortabilityDetailModal };
